import { create } from 'zustand';
import { api, ApiError } from '../api/client';
import type {
	GridRow,
	GridSlot,
	BatchToggleRequest,
	BatchToggleResponse,
	UndoResponse,
	RedoResponse,
} from '@spirtech/shared';

/*
   Translate a backend error into a French user-facing message. `error` is
   expected to be the ApiError thrown by api/client.ts — its `message` field
   carries the server's `body.error` value (a stable code) and `body` carries
   any structured payload alongside it.

   Cases covered today:
   - batch_too_large (HTTP 413) — the gesture exceeded the per-call cap. The
     server returns { limit, received } in the body so the toast can quote
     both numbers; the user knows whether to break the gesture in two or in
     ten.
   Default: hand the raw message back so unknown errors at least carry the
   server's wording instead of disappearing.
*/
function describeActionError(inErr: unknown): string {
	if (inErr instanceof ApiError) {
		if (inErr.message === 'batch_too_large') {
			const vBody = inErr.body ?? {};
			const vLimit = typeof vBody.limit === 'number' ? vBody.limit : 1500;
			const vRecv = typeof vBody.received === 'number' ? vBody.received : null;
			return vRecv != null
				? `Sélection trop volumineuse : ${vRecv} cellules en une seule opération, le maximum est ${vLimit}. Découpez votre saisie en plusieurs gestes.`
				: `Sélection trop volumineuse (au plus ${vLimit} cellules par opération). Découpez votre saisie en plusieurs gestes.`;
		}
		return inErr.message || 'Erreur inattendue';
	}
	return (inErr as { message?: string })?.message ?? 'Erreur inattendue';
}

export interface PendingSlot {
	userId: number;
	taskId: number;
	entryDate: string;
	hourSlot: number;
	filled: boolean;
}

interface UndoEntry {
	changeId: number;
}

interface GridState {
	rows: GridRow[];
	slots: Set<string>;
	conflicts: Set<string>;
	dateFrom: string;
	dateTo: string;
	showAll: boolean;
	loading: boolean;
	/* Fatal load error — populated only by load() failures. When set, the page
     replaces the grid with an error fallback because we don't have the data
     to render anything else. */
	error: string | null;
	/* Transient action error — populated when commit/undo/redo fails. Keeps
     the grid mounted; TimeGrid surfaces this as a dismissible banner so the
     user can retry without losing context. */
	actionError: string | null;
	undoStack: UndoEntry[];
	redoStack: UndoEntry[];

	load: (from?: string, to?: string, showAll?: boolean) => Promise<void>;
	commitGesture: (slots: PendingSlot[]) => Promise<void>;
	undo: () => Promise<void>;
	redo: () => Promise<void>;
	clearActionError: () => void;
}

function slotKey(userId: number, taskId: number, date: string, hour: number) {
	return `${userId}:${taskId}:${date}:${hour}`;
}

function buildConflictSet(allSlots: Set<string>): Set<string> {
	// Group by userId:date:hour — if >1 taskId fills the same slot → conflict
	const index = new Map<string, string[]>();
	for (const key of allSlots) {
		const [userId, , date, hour] = key.split(':');
		const groupKey = `${userId}:${date}:${hour}`;
		if (!index.has(groupKey)) index.set(groupKey, []);
		index.get(groupKey)!.push(key);
	}
	const conflicts = new Set<string>();
	for (const keys of index.values()) {
		if (keys.length > 1) keys.forEach((k) => conflicts.add(k));
	}
	return conflicts;
}

export const useGridStore = create<GridState>((set, get) => {
	/** Reload rows+slots from the server without showing the loading spinner.
	 *  Used after undo/redo when a previously-hidden row may have reappeared. */
	async function reloadSilent() {
		const { dateFrom, dateTo, showAll } = get();
		try {
			const qs = new URLSearchParams();
			if (dateFrom) qs.set('from', dateFrom);
			if (dateTo) qs.set('to', dateTo);
			if (showAll) qs.set('showAll', '1');
			const payload = await api.get<{
				rows: GridRow[];
				slots: GridSlot[];
				dateFrom: string;
				dateTo: string;
				undoStack: { changeId: number }[];
				redoStack: { changeId: number }[];
			}>(`/grid?${qs}`);
			const slots = new Set<string>(
				payload.slots.map((s) => slotKey(s.userId, s.taskId, s.entryDate, s.hourSlot)),
			);
			set({
				rows: payload.rows,
				slots,
				conflicts: buildConflictSet(slots),
				undoStack: payload.undoStack ?? [],
				redoStack: payload.redoStack ?? [],
			});
		} catch {
			// Silently ignore — the slot state is already correct, only rows may be stale
		}
	}

	return {
		rows: [],
		slots: new Set(),
		conflicts: new Set(),
		dateFrom: '',
		dateTo: '',
		showAll: false,
		loading: false,
		error: null,
		actionError: null,
		undoStack: [],
		redoStack: [],

		clearActionError() {
			set({ actionError: null });
		},

		async load(from, to, showAll?) {
			const effective = showAll ?? get().showAll;
			set({ loading: true, error: null, showAll: effective });
			try {
				const qs = new URLSearchParams();
				if (from) qs.set('from', from);
				if (to) qs.set('to', to);
				if (effective) qs.set('showAll', '1');
				const payload = await api.get<{
					rows: GridRow[];
					slots: GridSlot[];
					dateFrom: string;
					dateTo: string;
					undoStack: { changeId: number }[];
					redoStack: { changeId: number }[];
				}>(`/grid?${qs}`);
				const slots = new Set<string>(
					payload.slots.map((s) => slotKey(s.userId, s.taskId, s.entryDate, s.hourSlot)),
				);
				set({
					rows: payload.rows,
					slots,
					conflicts: buildConflictSet(slots),
					dateFrom: payload.dateFrom,
					dateTo: payload.dateTo,
					loading: false,
					undoStack: payload.undoStack ?? [],
					redoStack: payload.redoStack ?? [],
				});
			} catch (e: any) {
				set({ loading: false, error: e.message });
			}
		},

		async commitGesture(pending) {
			// Do NOT touch store.slots yet — the preview already provides visual feedback
			// while the request is in flight. Applying slots + conflicts in one update
			// after the server responds avoids a green→red flash caused by the client-side
			// conflict set disagreeing with the server's authoritative one.
			try {
				const body: BatchToggleRequest = {
					slots: pending.map((s) => ({
						userId: s.userId,
						taskId: s.taskId,
						entryDate: s.entryDate,
						hourSlot: s.hourSlot,
						filled: s.filled,
					})),
				};
				const result: BatchToggleResponse = await api.post('/time-entries/batch', body);

				// Build the new slots set from the current store state (unchanged during the call)
				const next = new Set(get().slots);
				for (const s of pending) {
					const key = slotKey(s.userId, s.taskId, s.entryDate, s.hourSlot);
					if (s.filled) next.add(key);
					else next.delete(key);
				}

				// Server conflicts are authoritative for affected users; preserve existing conflicts
				// for other visible users by computing client-side conflicts across all slots first.
				const allConflicts = buildConflictSet(next);
				for (const c of result.conflicts) {
					allConflicts.add(slotKey(c.userId, c.taskId, c.entryDate, c.hourSlot));
				}

				// Single update: slots + merged conflicts + history, one render. Clear
				// any stale actionError so the toast goes away on the next successful op.
				set((s) => ({
					slots: next,
					conflicts: allConflicts,
					undoStack: [{ changeId: result.changeId }, ...s.undoStack].slice(0, 100),
					redoStack: [],
					actionError: null,
				}));
			} catch (e: unknown) {
				// Slots were never optimistically changed, so no rollback needed —
				// surface the failure as a transient banner instead of replacing the grid.
				set({ actionError: describeActionError(e) });
			}
		},

		async undo() {
			const { undoStack, slots, rows } = get();
			if (!undoStack.length) return;
			const [last, ...rest] = undoStack;

			try {
				const result: UndoResponse = await api.post('/time-entries/undo', { changeId: last.changeId });
				const undone = new Set(slots);
				for (const s of result.undone) {
					const key = slotKey(s.userId, s.taskId, s.entryDate, s.hourSlot);
					if (s.action === 'add') undone.add(key);
					if (s.action === 'remove') undone.delete(key);
				}
				set((s) => ({
					slots: undone,
					conflicts: buildConflictSet(undone),
					undoStack: rest,
					redoStack: [last, ...s.redoStack],
					actionError: null,
				}));

				// If any restored slot belongs to a row not currently visible, reload to show it
				const rowPairs = new Set(rows.map((r) => `${r.userId}:${r.taskId}`));
				if (result.undone.some((s) => s.action === 'add' && !rowPairs.has(`${s.userId}:${s.taskId}`))) {
					await reloadSilent();
				}
			} catch (e: unknown) {
				set({ actionError: describeActionError(e) });
			}
		},

		async redo() {
			const { redoStack, slots, rows } = get();
			if (!redoStack.length) return;
			const [next, ...rest] = redoStack;

			try {
				const result: RedoResponse = await api.post('/time-entries/redo', { changeId: next.changeId });
				const redone = new Set(slots);
				for (const s of result.redone) {
					const key = slotKey(s.userId, s.taskId, s.entryDate, s.hourSlot);
					if (s.action === 'add') redone.add(key);
					if (s.action === 'remove') redone.delete(key);
				}
				set((s) => ({
					slots: redone,
					conflicts: buildConflictSet(redone),
					redoStack: rest,
					undoStack: [next, ...s.undoStack],
					actionError: null,
				}));

				// If any restored slot belongs to a row not currently visible, reload to show it
				const rowPairs = new Set(rows.map((r) => `${r.userId}:${r.taskId}`));
				if (result.redone.some((s) => s.action === 'add' && !rowPairs.has(`${s.userId}:${s.taskId}`))) {
					await reloadSilent();
				}
			} catch (e: unknown) {
				set({ actionError: describeActionError(e) });
			}
		},
	};
});
